import { cloneDeep } from 'lodash';
// import { v4 as uuid } from 'uuid';
import { enhanceBridge, enhanceGLBridge, fixZipResUrl, maskSmearBridge } from '@meitu/wasm-lib';
import assetsConfig from '@meitu/wasm-lib/assetsConfig.json';
import type { MTIKMaskSmearFilter } from '@meitu/wasm-lib/typings/lib/filter/MTIKMaskSmearFilter';
import { getFramework, trackConfig, type IFramework, type IGetSrcImage } from '@/framework';
import { applyRenderedMainLayerURL } from '@/layers/main-layer/utils';
import { base64Kit } from '@/utils/base64-kit';
import { createCanvas, getImgInfoAsync } from '@/utils/file-kit';
import { glKit } from '@/utils/gl-kit';
// import { IImageLayerProps, IImageProps, ILayerProps } from '@/version-types';
import type { IImageLayerProps, IImageProps } from '@/version-types';
import { ALL_COLOR_LIST, COLOR_LIST, FILTER_MASK_COLOR, MASK_COLOR, PREVIEW_SIZE, SMEAR_PEN_SIZE } from './constants';
import { getIntelligentColorState, watchIntelligentColorChange, type IStore } from './states/intelligent-color-state';
import { INTELLIGENT_COLOR_CHANGE_EVENT, type IIntelligentEnhanceParams, type IIntelligentPreviewItem } from './types';
import {
    formatIntelligentColor,
    getColorParams,
    getGlParams,
    getHsl,
    getRatioImage,
    uint8arrayToCanvas,
} from './utils';

const assets = {
    id: 'Enhance',
    zip: assetsConfig.filter_Enhance,
};

const prevMaskInfo: { [key: string]: any } = {
    imageData: null,
    code: '',
};

export class IntelligentColorPlugin {
    /** 编辑器实例 */
    private framework: IFramework;
    private store: IStore;

    constructor(identity: string) {
        this.framework = getFramework(identity);
        this.store = getIntelligentColorState(identity);

        watchIntelligentColorChange(identity, (state) => {
            this.store = state;
        });
    }

    getStore() {
        return this.store;
    }

    watch(callback: Parameters<typeof watchIntelligentColorChange>['1']) {
        return watchIntelligentColorChange(this.framework.identity, callback);
    }

    async create(): Promise<HTMLCanvasElement | null> {
        const { framework } = this;
        const originIndex = framework.service.frame.getActiveFrameId();
        const { setOriginLayer } = this.store;
        const originLayer = framework.service.frame.getMainLayer(originIndex)!;
        const newLayer = cloneDeep(originLayer);

        setOriginLayer(originLayer);

        // 初始化gl数据
        const obj = this.framework.service.edgelessCanvas.getByLayerId(originLayer.id) as unknown as IGetSrcImage;
        if (!obj || !obj.getSrcImage) {
            throw new Error('===>未找到对应的无限画布对象');
        }

        console.log('===>getSrcImage', obj);
        const rawCanvas = await obj.getSrcImage();
        glKit.openGL(newLayer);
        const fromCanvas = glKit.scaleResourceIfNeeded(rawCanvas);
        const glCanvas = await enhanceGLBridge.from(fromCanvas).create(assets.id, fixZipResUrl(assets.zip));

        glKit.openGL(newLayer);

        this.setHslMode(1);
        framework.service.frame.addOrUpdateLayer(newLayer);
        this.store.setIsIntelligent(true);
        return glCanvas;
    }

    async destroy() {
        const { framework } = this;
        const originIndex = framework.service.frame.getActiveFrameId();
        const layer = cloneDeep(framework.service.frame.getMainLayer(originIndex)!);
        // 应用上一次gl的结果
        maskSmearBridge.enableRenderToView(false, false);
        const manager = await enhanceGLBridge.getManager();
        manager.processRender(true);

        await glKit.applyGLResult({
            layer: layer,
            props: layer.data as IImageProps,
            baseBridge: enhanceGLBridge,
            resultSize: {
                width: (layer.data as IImageProps).naturalWidth,
                height: (layer.data as IImageProps).naturalHeight,
            },
        });

        const latestLayer = framework.service.flatLayer.getLayer(layer.id, true);
        if (!latestLayer) {
            await this.reset();
            this.store.setIsIntelligent(false);
            this.store.setIsProtectPen(false);
            return;
        }
        const committedLayer = applyRenderedMainLayerURL(latestLayer, (layer.data as IImageProps).url);
        framework.service.frame.addOrUpdateLayer(committedLayer);

        // 退出时，如果主图没有变化，则不写入历史记录
        if ((latestLayer.data as IImageLayerProps).url !== (committedLayer.data as IImageLayerProps).url) {
            // 撤销恢复旧图片资源，不恢复已失效的 GL 预览状态。
            glKit.clearGL(latestLayer);
            framework.service.history.write(latestLayer, committedLayer, trackConfig.adjust.intelligentColor);
        }

        await this.reset();
        this.store.setIsIntelligent(false);
        this.store.setIsProtectPen(false);
    }

    async reset() {
        const scale = this.framework.service.edgelessCanvas.zoom;

        this.setHslMode(0);
        this.setSmearPenSize(SMEAR_PEN_SIZE / 2 / scale);
        this.stopSmear();
        this.store.intelligentDestroy();
        this.framework.service.events.emit(INTELLIGENT_COLOR_CHANGE_EVENT.Reset);
    }

    onReset(handleReset: () => void) {
        const handler = this.framework.service.events.on(INTELLIGENT_COLOR_CHANGE_EVENT.Reset, handleReset);

        return () => {
            this.framework.service.events.off(handler);
        };
    }

    resetColor() {
        this.framework.service.events.emit(INTELLIGENT_COLOR_CHANGE_EVENT.ResetColor);
    }

    onResetColor(handleReset: () => void) {
        const handler = this.framework.service.events.on(INTELLIGENT_COLOR_CHANGE_EVENT.ResetColor, handleReset);

        return () => {
            this.framework.service.events.off(handler);
        };
    }

    async restore() {
        const originLayer = this.store.originLayer!;

        await this.framework.service.frame.addOrUpdateLayer(originLayer);
        this.reset();
        setTimeout(() => {
            this.create();
        }, 0);
    }

    async initIntelligentColors() {
        const colors = await enhanceGLBridge.imageDetec();
        this.store.initIntelligentColors(formatIntelligentColor(colors).slice(0, 5));
    }

    private async setHslMode(mode: number) {
        enhanceGLBridge.setHSLMode(mode);
        enhanceBridge.setHSLMode(mode);
    }

    private async applyGLEnhance(data: IIntelligentEnhanceParams) {
        const params = getGlParams(data);
        await enhanceGLBridge.setParams(params);
    }

    async startSmear() {
        const originIndex = this.framework.service.frame.getActiveFrameId();
        const originLayer = this.framework.service.frame.getMainLayer(originIndex)!;
        console.log('===>startSmear', originLayer);

        const scale = this.framework.service.edgelessCanvas.zoom;
        const glManager = await enhanceGLBridge.getManager();
        await maskSmearBridge.setManager(glManager, false).create();
        enhanceGLBridge.startSmear(maskSmearBridge.getFilterInstance() as MTIKMaskSmearFilter);
        this.setMaskColor();
        this.setSmearPenSize(SMEAR_PEN_SIZE / 2 / scale);
        this.setFilterMaskColor();
    }

    async getMaskData() {
        console.time('===>getMaskData');
        const maskData = await maskSmearBridge.getMaskData();
        if (maskData) {
            const base64 = uint8arrayToCanvas(maskData);
            const code = await base64Kit.getCode(base64);
            console.timeEnd('===>getMaskData');
            return code;
        }
        return '';
    }

    private async setMaskData(code: string) {
        console.time('===>setMaskData');
        if (!code) return;
        if (code === prevMaskInfo.code) {
            const { imageData } = prevMaskInfo;
            const { data, width, height } = imageData;
            await maskSmearBridge.setMaskData(data, width, height);
            return;
        }
        const url = await base64Kit.getRemoteUrlAsync(code);
        const { width, height, img } = await getImgInfoAsync(url);
        const { ctx } = createCanvas(width, height, img);
        const data = ctx.getImageData(0, 0, width, height).data;
        await maskSmearBridge.setMaskData(data, width, height);
        prevMaskInfo.imageData = { data, width, height };
        prevMaskInfo.code = code;
        console.timeEnd('===>setMaskData');
    }

    private async setMaskColor() {
        enhanceGLBridge.setMaskColor(MASK_COLOR);
    }

    async setSmearPenSize(size: number) {
        enhanceGLBridge.setMaskPenSize(size);
    }

    async setFilterMaskColor() {
        const [r, g, b, a] = FILTER_MASK_COLOR;
        await maskSmearBridge.setMaskColor(r, g, b, a);
    }

    async setEraserType() {
        maskSmearBridge.setMaskSmearMode(0);
    }

    async setPenType() {
        maskSmearBridge.setMaskSmearMode(1);
    }

    async resetMask() {
        maskSmearBridge.reset();
    }

    async stopSmear() {
        const scale = this.framework.service.edgelessCanvas.zoom;
        this.setSmearPenSize(SMEAR_PEN_SIZE / 2 / scale);
        maskSmearBridge.destroy();
    }

    updateLayer() {
        const { framework } = this;
        const newLayer = cloneDeep(framework.service.flatLayer.getLayer(this.store.originLayer?.id!));
        glKit.processGL(newLayer);
        framework.service.frame.addOrUpdateLayer(newLayer);
    }

    async applyGLEnhanceAndUpdateLayer(data: IIntelligentEnhanceParams) {
        await this.applyGLEnhance(cloneDeep(data));
        this.updateLayer();
    }

    async getHSLFromColors(from: string, target: string) {
        const hslPickColors = await enhanceGLBridge.getHSLFromColors(
            from.split(',').map((v) => +v / 255),
            target.split(',').map((v) => +v / 255),
        );
        return hslPickColors;
    }

    private async getColorPreviewData(
        pickColors: string,
        hslPickColors: number[] | [],
        isHslSelectAll: boolean = false,
        color: any = {},
    ) {
        const params = getGlParams({ pickColors, hslPickColors, isHslSelectAll, color });

        enhanceBridge.setParams(params);
        const { canvas } = await enhanceBridge.getGLResult();
        return canvas?.toDataURL() || '';
    }

    async renderIntelligentPreview(color: string) {
        console.time('renderIntelligentPreview');
        const { framework } = this;
        const originIndex = framework.service.frame.getActiveFrameId();
        try {
            const tempArr: IIntelligentPreviewItem[] = [];
            const layer = framework.service.frame.getMainLayer(originIndex)!;
            const data = layer.data as IImageLayerProps;
            if (!data.url) return;
            const canvas = await getRatioImage(data.url);
            await enhanceBridge.from(canvas).doEnhance(assets.id, fixZipResUrl(assets.zip));
            enhanceBridge.setHSLMode(1);
            const maskInstance = maskSmearBridge.getFilterInstance();
            if (maskInstance) {
                enhanceBridge.startSmear(maskInstance);
            }
            maskSmearBridge.enableRenderToView(false, false);

            for (const item of COLOR_LIST) {
                const hslPickColors = await this.getHSLFromColors(color, item);
                const base64 = await this.getColorPreviewData(color, hslPickColors);
                tempArr.push({
                    url: base64,
                    hslPickColors,
                    pickColors: color,
                    color: item,
                    fuzziness: 0,
                });
            }

            tempArr.unshift({
                url: canvas.toDataURL(),
                hslPickColors: [0, 0, 0],
                pickColors: '',
                color: '',
                fuzziness: 0,
            });
            this.store.setIntelligentPreviewList(tempArr);
            maskSmearBridge.enableRenderToView(true, false);
            await enhanceBridge.stopSmear(maskSmearBridge.getFilterInstance() as MTIKMaskSmearFilter);
            enhanceBridge.destroy();
        } catch (e) {
            console.log('renderIntelligentPreview error ', e);
        }
        console.timeEnd('renderIntelligentPreview');
    }

    async renderAllIntelligentPreview(maskCode?: string) {
        console.time('renderAllIntelligentPreview');
        const { framework } = this;
        const originIndex = framework.service.frame.getActiveFrameId();
        try {
            const tempArr: IIntelligentPreviewItem[] = [];
            const layer = framework.service.frame.getMainLayer(originIndex)!;
            const data = layer.data as IImageLayerProps;
            if (!data.url) return;
            const canvas = await getRatioImage(data.url);
            await enhanceBridge.from(canvas).doEnhance(assets.id, fixZipResUrl(assets.zip));
            enhanceBridge.setHSLMode(1);
            const maskInstance = maskSmearBridge.getFilterInstance();
            if (maskInstance) {
                enhanceBridge.startSmear(maskInstance);
            }
            if (maskCode) {
                await this.setMaskData(maskCode);
            } else if (maskCode === '') {
                await this.resetMask();
            }
            maskSmearBridge.enableRenderToView(false, false);

            for (const item of ALL_COLOR_LIST) {
                const color = getColorParams(item);
                const hslPickColors = getHsl(item);
                const base64 = await this.getColorPreviewData('', [], true, color);
                tempArr.push({
                    url: base64,
                    hslPickColors,
                    pickColors: '',
                    color: item,
                });
            }

            tempArr.unshift({
                url: canvas.toDataURL(),
                hslPickColors: [0, 0, 0],
                pickColors: '',
                color: '',
            });
            this.store.setIntelligentPreviewList(tempArr);

            maskSmearBridge.enableRenderToView(true, false);

            await enhanceBridge.stopSmear(maskSmearBridge.getFilterInstance() as MTIKMaskSmearFilter);
            enhanceBridge.destroy();
        } catch (e) {
            console.log('renderAllIntelligentPreview error ', e);
        }
        console.timeEnd('renderAllIntelligentPreview');
    }
}
